import React, {useEffect} from 'react';
import {useWindowSize} from "@uidotdev/usehooks";
import {useSelector, useDispatch} from "react-redux";
import {useNavigate} from "react-router-dom";
import {Flex, Row, Col, Tabs, Button} from "antd";
import {LogoutOutlined} from "@ant-design/icons";
import IntlMessage from "../../components/IntlMessage/IntlMessage";
import Header from "../components/Header/Header";
import MyText from "../../components/UI/Text/MyText";
import {clrs} from "../../constants/colors";
import Footer from "../components/Footer/Footer";
import {clearUser} from "../../store/slices/userSlice";

// Admin Components
import CreateUser from "./components/Admin/CreateUser";
import UserList from "./components/Admin/UserList";
import Application from "./components/Admin/Application";
import WaterConsumption from "./components/Admin/WaterConsumption";

// User Components
import WaterStatus from "./components/User/WaterStatus";
import UserProfile from "./components/User/UserProfile";
import UserApplications from "./components/User/UserApplications";
import Monitoring from "./components/Admin/Monitoring";
import KordaiMonitoring from "./components/Admin/KordaiMonitoring";
import TalasDashboard from "./components/Admin/TalasDashboard";

const DashboardPage = () => {
    const size = useWindowSize();
    const { user, isLoading } = useSelector(state => state.user);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        if (!user) {
            navigate('/login', { replace: true });
        }
    }, [user, navigate]);

    const handleLogout = () => {
        localStorage.removeItem('Authorization');
        localStorage.removeItem('ExpirationDate');
        dispatch(clearUser());
        navigate('/login', { replace: true });
    };

    const renderAdminDashboard = () => (
        <>
            <Flex style={{ width: '100%', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <MyText style={{ fontSize: "2rem", fontWeight: 800, color: clrs.textColor }}>
                    <IntlMessage id={'dashboard.admin.title'} fallback={'Панель администратора'} />
                </MyText>
                <Button 
                    type="primary" 
                    danger
                    icon={<LogoutOutlined />}
                    onClick={handleLogout}
                >
                    <IntlMessage id={'dashboard.logout'} fallback={'Выйти'} />
                </Button>
            </Flex>
            
            <Tabs defaultActiveKey="overview" style={{ width: '100%' }}>
                <Tabs.TabPane 
                    tab={<IntlMessage id={'dashboard.admin.overview'} fallback={'Заявки'} />}
                    key="overview"
                >
                    <Row gutter={[24, 24]}>
                        <Col xs={24}>
                            <Application />
                        </Col>
                    </Row>
                </Tabs.TabPane>
                
                <Tabs.TabPane 
                    tab={<IntlMessage id={'dashboard.admin.users'} fallback={'Управление пользователями'} />} 
                    key="users"
                >
                    <Row gutter={[24, 24]}>
                        <Col xs={24} xl={8}>
                            <CreateUser />
                        </Col>
                        <Col xs={24} xl={16}>
                            <UserList />
                        </Col>
                    </Row>
                </Tabs.TabPane>
                
                <Tabs.TabPane
                    tab={<IntlMessage id={'dashboard.admin.analytics'} fallback={'Мониторинг'} />}
                    key="analytics"
                >
                    <Row gutter={[24, 24]}>
                        <Col xs={24}>
                            <Monitoring/>
                        </Col>
                    </Row>
                </Tabs.TabPane>

                <Tabs.TabPane
                    tab={<IntlMessage id={'dashboard.admin.kordaiAnalytics'} fallback={'Мониторинг Кордай'} />}
                    key="kordaiAnalytics"
                >
                    <Row gutter={[24, 24]}>
                        <Col xs={24}>
                            <KordaiMonitoring/>
                        </Col>
                    </Row>
                </Tabs.TabPane>

                <Tabs.TabPane
                    tab={<IntlMessage id={'dashboard.admin.talasInfographic'} fallback={'Инфографика Талас'} />}
                    key="talasInfographic"
                >
                    <Row gutter={[24, 24]}>
                        <Col xs={24}>
                            <TalasDashboard/>
                        </Col>
                    </Row>
                </Tabs.TabPane>
            </Tabs>
        </>
    );

    const renderMonitoringDashboard = () => (
        <>
            <Flex style={{ width: '100%', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <MyText style={{ fontSize: "2rem", fontWeight: 800, color: clrs.textColor }}>
                    <IntlMessage id={'dashboard.kazhydromet.title'} fallback={'Панель мониторинга'} />
                </MyText>
                <Button 
                    type="primary" 
                    danger
                    icon={<LogoutOutlined />}
                    onClick={handleLogout}
                >
                    <IntlMessage id={'dashboard.logout'} fallback={'Выйти'} />
                </Button>
            </Flex>
            
            <Tabs defaultActiveKey="monitoring" style={{ width: '100%' }}>
                <Tabs.TabPane
                    tab={<IntlMessage id={'dashboard.admin.analytics'} fallback={'Мониторинг'} />}
                    key="monitoring"
                >
                    <Row gutter={[24, 24]}>
                        <Col xs={24}>
                            <Monitoring/>
                        </Col>
                    </Row>
                </Tabs.TabPane>

                <Tabs.TabPane
                    tab={<IntlMessage id={'dashboard.admin.kordaiAnalytics'} fallback={'Мониторинг Кордай'} />}
                    key="kordaiAnalytics"
                >
                    <Row gutter={[24, 24]}>
                        <Col xs={24}>
                            <KordaiMonitoring/>
                        </Col>
                    </Row>
                </Tabs.TabPane>

                <Tabs.TabPane
                    tab={<IntlMessage id={'dashboard.admin.talasInfographic'} fallback={'Инфографика Талас'} />}
                    key="talasInfographic"
                >
                    <Row gutter={[24, 24]}>
                        <Col xs={24}>
                            <TalasDashboard/>
                        </Col>
                    </Row>
                </Tabs.TabPane>
            </Tabs>
        </>
    );

    const renderUserDashboard = () => (
        <>
            <Flex style={{ width: '100%', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <MyText style={{ fontSize: "2rem", fontWeight: 800, color: clrs.textColor }}>
                    <IntlMessage id={'dashboard.user.title'} fallback={'Панель пользователя'} />
                </MyText>
                <Button 
                    type="primary" 
                    danger
                    icon={<LogoutOutlined />}
                    onClick={handleLogout}
                >
                    <IntlMessage id={'dashboard.logout'} fallback={'Выйти'} />
                </Button>
            </Flex>
            
            <Tabs defaultActiveKey="profile" style={{ width: '100%' }}>
                <Tabs.TabPane 
                    tab={<IntlMessage id={'dashboard.user.profile'} fallback={'Профиль'} />}
                    key="profile"
                >
                    <Row gutter={[24, 24]}>
                        <Col xs={24}>
                            <UserProfile />
                        </Col>
                    </Row>
                </Tabs.TabPane>
                
                <Tabs.TabPane 
                    tab={<IntlMessage id={'dashboard.user.applications'} fallback={'Заявки'} />}
                    key="applications"
                >
                    <Row gutter={[24, 24]}>
                        <Col xs={24}>
                            <UserApplications />
                        </Col>
                    </Row>
                </Tabs.TabPane>
            </Tabs>
        </>
    );

    return (
        <div>
            <Header />
            <Flex style={{ width: "100%", position: "relative", padding: "150px 20px 20px 20px" }} vertical align={"center"} justify={"center"}>
                <Flex style={{ width: '100%', maxWidth: 1440, position: 'relative', margin: '0 auto' }} gap={24} vertical align={'center'} justify={'flex-start'}>
                    {user?.role?.roleName === 'admin' 
                        ? renderAdminDashboard() 
                        : user?.role?.roleName === 'monitoring'
                        ? renderMonitoringDashboard()
                        : renderUserDashboard()}
                </Flex>
            </Flex>
            <Footer />
        </div>
    );
};

export default DashboardPage;